// src/components/modals/MobileAppAccessModal.jsx
import React, { useState, useEffect } from 'react';
import { get, post, patch, del } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { 
    FaSpinner, 
    FaPlus, 
    FaCheckCircle, 
    FaTimesCircle, 
    FaKey, 
    FaMobileAlt,
    FaInfoCircle,
    FaGlobe
} from 'react-icons/fa';

const MobileAppAccessModal = ({ isOpen, onClose, tiendaId, tiendaNombre }) => {
    const [accesses, setAccesses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        metadata: {
            deviceType: 'web'
        }
    });

    useEffect(() => {
        if (isOpen && tiendaId) {
            fetchAccesses();
        }
    }, [isOpen, tiendaId]);

    const fetchAccesses = async () => {
        try {
            setLoading(true);
            const response = await get(`mobile-app/accesses?tiendaId=${tiendaId}`);
            setAccesses(response?.accesses || response || []);
        } catch (error) {
            console.error('Error fetching accesses:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'Error al cargar accesos móviles',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            setAccesses([]);
        } finally {
            setLoading(false);
        }
    };


    const handleCreateAccess = async (e) => {
        e.preventDefault();
        
        if (!formData.nombre.trim()) {
            Swal.fire({
                title: 'Error',
                text: 'El nombre es requerido',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        try {
            setLoading(true);
            const dataToSend = {
                tiendaId,
                nombre: formData.nombre,
                descripcion: formData.descripcion || undefined,
                metadata: {
                    deviceType: 'web'
                }
            };

            // Eliminar campos undefined
            Object.keys(dataToSend).forEach(key => {
                if (dataToSend[key] === undefined) {
                    delete dataToSend[key];
                }
            });

            const response = await post('mobile-app/accesses', dataToSend);
            const access = response?.access || response;
            const token = access?.token || response?.token;

            // Mostrar token al usuario (solo se muestra una vez)
            if (token) {
                Swal.fire({
                    title: '¡Acceso creado exitosamente!',
                    html: `
                        <div class="text-left">
                            <p class="mb-3"><strong>⚠️ IMPORTANTE:</strong> Guarda este token de forma segura. Solo se mostrará una vez.</p>
                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                                <p class="text-sm text-blue-800 mb-2">
                                    <strong>📱 Usa este token en:</strong>
                                </p>
                                <a href="https://escaner.petitprime.com" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline font-medium text-sm">
                                    escaner.petitprime.com
                                </a>
                            </div>
                            <div class="bg-gray-100 p-3 rounded mb-3 relative">
                                <code id="tokenDisplay" class="text-sm break-all font-mono select-none">${'•'.repeat(token.length)}</code>
                                <button id="toggleTokenVisibility" class="absolute right-3 top-3 text-gray-600 hover:text-gray-800 transition-colors p-1" title="Mostrar/Ocultar token">
                                    <svg id="eyeIcon" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                    </svg>
                                </button>
                            </div>
                            <div class="flex gap-2">
                                <button id="copyToken" class="bg-blue-500 text-white px-4 py-2 rounded text-sm flex-1 hover:bg-blue-600 transition-colors">
                                    Copiar Token
                                </button>
                                <button id="showToken" class="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600 transition-colors">
                                    Mostrar Token
                                </button>
                            </div>
                        </div>
                    `,
                    icon: 'success',
                    width: '600px',
                    showConfirmButton: true,
                    confirmButtonText: 'Cerrar',
                    didOpen: () => {
                        let tokenVisible = false;
                        const tokenDisplay = document.getElementById('tokenDisplay');
                        const toggleButton = document.getElementById('toggleTokenVisibility');
                        const showButton = document.getElementById('showToken');
                        const eyeIcon = document.getElementById('eyeIcon');
                        
                        // Función para alternar visibilidad
                        const toggleVisibility = () => {
                            tokenVisible = !tokenVisible;
                            if (tokenVisible) {
                                tokenDisplay.textContent = token;
                                eyeIcon.innerHTML = `
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                                `;
                                if (showButton) showButton.textContent = 'Ocultar Token';
                                } else {
                                    tokenDisplay.textContent = '•'.repeat(token.length);
                                    eyeIcon.innerHTML = `
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                    `;
                                    if (showButton) showButton.textContent = 'Mostrar Token';
                                }
                        };
                        
                        // Botón toggle (icono ojo)
                        toggleButton?.addEventListener('click', toggleVisibility);
                        
                        // Botón mostrar/ocultar
                        showButton?.addEventListener('click', toggleVisibility);
                        
                        // Botón copiar
                        document.getElementById('copyToken')?.addEventListener('click', () => {
                            navigator.clipboard.writeText(token).then(() => {
                                Swal.fire({
                                    title: 'Copiado!',
                                    text: 'Token copiado al portapapeles',
                                    icon: 'success',
                                    timer: 2000,
                                    showConfirmButton: false
                                });
                            });
                        });
                    }
                });
            }

            // Resetear formulario
            setFormData({
                nombre: '',
                descripcion: '',
                metadata: {
                    deviceType: 'web'
                }
            });
            setShowCreateForm(false);
            fetchAccesses();
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'Error al crear acceso móvil',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAccess = async (accessId, currentStatus) => {
        const action = currentStatus ? 'desactivar' : 'activar';
        const result = await Swal.fire({
            title: `¿${action.charAt(0).toUpperCase() + action.slice(1)} acceso?`,
            text: currentStatus 
                ? 'El acceso dejará de funcionar inmediatamente'
                : 'El acceso volverá a estar activo',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `Sí, ${action}`,
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                if (currentStatus) {
                    await del(`mobile-app/accesses/${accessId}`);
                } else {
                    await patch(`mobile-app/accesses/${accessId}/activate`);
                }
                Swal.fire({
                    title: 'Éxito',
                    text: `Acceso ${currentStatus ? 'desactivado' : 'activado'} correctamente`,
                    icon: 'success',
                    confirmButtonText: 'Aceptar'
                });
                fetchAccesses();
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: error.message || `Error al ${action} el acceso`,
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            } finally {
                setLoading(false);
            }
        }
    };

    const handleRegenerateToken = async (accessId, accessNombre) => {
        const result = await Swal.fire({
            title: '¿Regenerar token?',
            html: `
                <div class="text-left">
                    <p class="mb-2">El token actual será invalidado inmediatamente.</p>
                                <p class="mb-2"><strong>Acceso:</strong> ${accessNombre}</p>
                                <p class="text-sm text-orange-600">⚠️ Deberás actualizar la aplicación web con el nuevo token.</p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, regenerar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await post(`mobile-app/accesses/${accessId}/regenerate-token`);
                const newToken = response?.newToken || response?.token;

                if (newToken) {
                    Swal.fire({
                        title: '¡Token regenerado!',
                        html: `
                            <div class="text-left">
                                <p class="mb-3"><strong>⚠️ IMPORTANTE:</strong> El token anterior ya no funciona. Actualiza la aplicación web con este nuevo token.</p>
                                <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                                    <p class="text-sm text-blue-800 mb-2">
                                        <strong>📱 Usa este token en:</strong>
                                    </p>
                                    <a href="https://escaner.petitprime.com" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline font-medium text-sm">
                                        escaner.petitprime.com
                                    </a>
                                </div>
                                <div class="bg-gray-100 p-3 rounded mb-3 relative">
                                    <code id="newTokenDisplay" class="text-sm break-all font-mono select-none">${'•'.repeat(newToken.length)}</code>
                                    <button id="toggleNewTokenVisibility" class="absolute right-3 top-3 text-gray-600 hover:text-gray-800 transition-colors p-1" title="Mostrar/Ocultar token">
                                        <svg id="newEyeIcon" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                        </svg>
                                    </button>
                                </div>
                                <div class="flex gap-2">
                                    <button id="copyNewToken" class="bg-blue-500 text-white px-4 py-2 rounded text-sm flex-1 hover:bg-blue-600 transition-colors">
                                        Copiar Nuevo Token
                                    </button>
                                    <button id="showNewToken" class="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600 transition-colors">
                                        Mostrar Token
                                    </button>
                                </div>
                            </div>
                        `,
                        icon: 'success',
                        width: '600px',
                        showConfirmButton: true,
                        confirmButtonText: 'Cerrar',
                        didOpen: () => {
                            let tokenVisible = false;
                            const tokenDisplay = document.getElementById('newTokenDisplay');
                            const toggleButton = document.getElementById('toggleNewTokenVisibility');
                            const showButton = document.getElementById('showNewToken');
                            const eyeIcon = document.getElementById('newEyeIcon');
                            
                            // Función para alternar visibilidad
                            const toggleVisibility = () => {
                                tokenVisible = !tokenVisible;
                                if (tokenVisible) {
                                    tokenDisplay.textContent = newToken;
                                    eyeIcon.innerHTML = `
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                                    `;
                                    if (showButton) showButton.textContent = 'Ocultar Token';
                                } else {
                                    tokenDisplay.textContent = '•'.repeat(newToken.length);
                                    eyeIcon.innerHTML = `
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                    `;
                                    if (showButton) showButton.textContent = 'Mostrar Token';
                                }
                            };
                            
                            // Botón toggle (icono ojo)
                            toggleButton?.addEventListener('click', toggleVisibility);
                            
                            // Botón mostrar/ocultar
                            showButton?.addEventListener('click', toggleVisibility);
                            
                            // Botón copiar
                            document.getElementById('copyNewToken')?.addEventListener('click', () => {
                                navigator.clipboard.writeText(newToken).then(() => {
                                    Swal.fire({
                                        title: 'Copiado!',
                                        text: 'Token copiado al portapapeles',
                                        icon: 'success',
                                        timer: 2000,
                                        showConfirmButton: false
                                    });
                                });
                            });
                        }
                    });
                }
                fetchAccesses();
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: error.message || 'Error al regenerar token',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            } finally {
                setLoading(false);
            }
        }
    };


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4">
                <div className="fixed inset-0 transition-opacity" onClick={onClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-4xl mx-auto relative z-10">
                    <div className="px-6 pt-6 pb-4 max-h-[90vh] overflow-y-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                                    <FaGlobe className="w-5 h-5 mr-2 text-blue-500" />
                                    Accesos Web - {tiendaNombre}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Gestiona los accesos para la aplicación web que escanea QR y acumula puntos
                                </p>
                            </div>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Información */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 mb-4">
                            <div className="flex items-start">
                                <FaInfoCircle className="text-blue-500 mt-1 mr-2 flex-shrink-0" />
                                <div className="text-sm text-blue-600">
                                    <p className="mb-1">
                                        <strong>¿Qué son los accesos web?</strong>
                                    </p>
                                    <p>
                                        Los accesos web permiten que la aplicación web escanee códigos QR y acumule puntos 
                                        sin necesidad de acceder al dashboard completo. Cada acceso tiene un token único 
                                        que debe configurarse en la aplicación web. Encontrarás el enlace a la aplicación 
                                        de escaneo en cada acceso de la lista.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Botón crear nuevo acceso */}
                        {!showCreateForm && (
                            <div className="mb-4">
                                <button
                                    onClick={() => setShowCreateForm(true)}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                                >
                                    <FaPlus className="mr-2" />
                                    Crear Nuevo Acceso
                                </button>
                            </div>
                        )}

                        {/* Formulario crear acceso */}
                        {showCreateForm && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Crear Nuevo Acceso</h4>
                                <form onSubmit={handleCreateAccess}>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Nombre del Acceso *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.nombre}
                                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Ej: App Web Caja Principal, App Web Recepción"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Descripción
                                            </label>
                                            <textarea
                                                value={formData.descripcion}
                                                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Descripción opcional del acceso"
                                                rows={2}
                                            />
                                        </div>


                                        <div className="flex gap-3">
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center"
                                            >
                                                {loading ? (
                                                    <>
                                                        <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                                        Creando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaPlus className="mr-2" />
                                                        Crear Acceso
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowCreateForm(false);
                                                    setFormData({
                                                        nombre: '',
                                                        descripcion: '',
                                                        metadata: {
                                                            deviceType: 'web'
                                                        }
                                                    });
                                                }}
                                                className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Lista de accesos */}
                        {loading && accesses.length === 0 ? (
                            <div className="text-center py-8">
                                <FaSpinner className="animate-spin text-blue-500 text-4xl mx-auto mb-4" />
                                <p className="text-gray-600">Cargando accesos...</p>
                            </div>
                        ) : accesses.length === 0 ? (
                            <div className="text-center py-8">
                                <FaGlobe className="text-3xl text-gray-300 mx-auto mb-3" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay accesos creados</h3>
                                <p className="text-gray-500 mb-4">Crea tu primer acceso web para comenzar</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {accesses.map((access) => (
                                    <div key={access._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h4 className="text-lg font-semibold text-gray-900">{access.nombre}</h4>
                                                    {access.activo ? (
                                                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                                                            <FaCheckCircle />
                                                            Activo
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">
                                                            <FaTimesCircle />
                                                            Inactivo
                                                        </span>
                                                    )}
                                                </div>

                                                {access.descripcion && (
                                                    <p className="text-sm text-gray-600 mb-3">{access.descripcion}</p>
                                                )}

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                                    {access.usageCount !== undefined && (
                                                        <div>
                                                            <span className="text-gray-500">Usos:</span>
                                                            <p className="font-medium">{access.usageCount}</p>
                                                        </div>
                                                    )}
                                                    {access.lastUsed && (
                                                        <div>
                                                            <span className="text-gray-500">Último uso:</span>
                                                            <p className="font-medium">
                                                                {new Date(access.lastUsed).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {access.metadata && access.metadata.deviceType && (
                                                        <div className="md:col-span-2">
                                                            <span className="text-gray-500">Tipo:</span>
                                                            <span className="ml-2 bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                                                                {access.metadata.deviceType === 'web' ? 'Aplicación Web' : access.metadata.deviceType}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {/* Enlace prominente a la app de escaneo */}
                                                <div className="mt-4 pt-4 border-t border-gray-200">
                                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex-1">
                                                                <p className="text-sm font-semibold text-blue-900 mb-1">
                                                                    📱 Aplicación de Escaneo
                                                                </p>
                                                                <p className="text-xs text-blue-700 mb-2">
                                                                    Usa este acceso en la aplicación web para escanear QR y acumular puntos
                                                                </p>
                                                                <a 
                                                                    href="https://escaner.petitprime.com" 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer" 
                                                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-bold text-base transition-colors"
                                                                >
                                                                    <FaGlobe className="w-5 h-5" />
                                                                    escaner.petitprime.com
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                    </svg>
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-2 ml-4">
                                                <button
                                                    onClick={() => handleToggleAccess(access._id, access.activo)}
                                                    className={`px-3 py-1 rounded text-sm transition-colors flex items-center ${
                                                        access.activo
                                                            ? 'bg-red-500 text-white hover:bg-red-600'
                                                            : 'bg-green-500 text-white hover:bg-green-600'
                                                    }`}
                                                >
                                                    {access.activo ? (
                                                        <>
                                                            <FaTimesCircle className="mr-1" />
                                                            Desactivar
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaCheckCircle className="mr-1" />
                                                            Activar
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleRegenerateToken(access._id, access.nombre)}
                                                    className="px-3 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 transition-colors flex items-center"
                                                    title="Regenerar token (el anterior dejará de funcionar)"
                                                >
                                                    <FaKey className="mr-1" />
                                                    Regenerar Token
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MobileAppAccessModal;

